import * as React from "react";
import { Button, ActionIcon, Menu, Group, useMantineColorScheme, Tooltip } from "@mantine/core";
import { Settings, ChevronDown, Pencil, Filter, Play, Info, Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";
import { deleteChart, getFolderForItem } from "@bi-studio/shared/apis";
import { useAuth } from "@bi-studio/shared/hooks";
import { ConfirmationModal } from "@bi-studio/shared/components";
import { useDisclosure } from "@mantine/hooks";

export interface ChartActionsButtonProps {
    chartId: string;
    report: any;
    variableValues: Record<string, string>;
    isLoading: boolean;
    isExecuting: boolean;
    runChart: (values?: Record<string, string>) => void;
    setIsVariablesModalOpen: (open: boolean) => void;
    handleViewDetails: () => void;
}

export function ChartActionsButton({
    chartId,
    report,
    variableValues,
    isLoading,
    isExecuting,
    runChart,
    setIsVariablesModalOpen,
    handleViewDetails
}: ChartActionsButtonProps) {
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();
    const { token } = useAuth();
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleConfirmDelete = async () => {
        if (!token) return;
        try {
            setIsDeleting(true);
            const folderInfo = await getFolderForItem(token, "chart", chartId);
            await deleteChart(token, chartId);
            closeDelete();
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (error) {
            console.error("Failed to delete chart:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Group gap="xs">

            <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                    <Group gap={0}>
                        <Button
                            visibleFrom="sm"
                            color="black"
                            c="white"
                            leftSection={<Settings size={16} />}
                            rightSection={<ChevronDown size={14} />}
                        >
                            Actions
                        </Button>
                        <Tooltip label="Actions">
                            <ActionIcon
                                hiddenFrom="sm"
                                variant="filled"
                                color="black"
                                c="white"
                                size="lg"
                            >
                                <Settings size={20} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Menu.Target>

                <Menu.Dropdown
                    style={{
                        backgroundColor: colorScheme === 'dark' ? 'var(--modal-bg)' : undefined,
                        borderColor: colorScheme === 'dark' ? 'var(--modal-border)' : undefined,
                        backdropFilter: 'blur(var(--modal-blur))'
                    }}
                >

                    {report && (report.variables || []).filter((v: any) => !PREDEFINED_VARIABLES.includes(v.name)).length > 0 && (
                        <Menu.Item
                            leftSection={<Filter size={16} />}
                            onClick={() => setIsVariablesModalOpen(true)}
                        >
                            Change Variables
                        </Menu.Item>
                    )}
                    <Menu.Item
                        leftSection={<Play size={16} />}
                        onClick={() => runChart(variableValues)}
                        disabled={isLoading || isExecuting}
                    >
                        {isExecuting ? "Running..." : "Run"}
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<Info size={16} />}
                        onClick={handleViewDetails}
                    >
                        View Details
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        leftSection={<Pencil size={16} />}
                        onClick={() => navigate(`/charts/${chartId}/edit`)}
                    >
                        Edit
                    </Menu.Item>
                    <Menu.Item
                        c="red"
                        leftSection={<Trash size={16} />}
                        onClick={openDelete}
                    >
                        Delete
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            <ConfirmationModal
                opened={deleteOpened}
                onClose={closeDelete}
                onConfirm={handleConfirmDelete}
                title="Delete Chart"
                confirmLabel="Delete"
                confirmColor="red"
                loading={isDeleting}
            >
                Are you sure you want to delete this chart? This action cannot be undone.
            </ConfirmationModal>
        </Group>
    );
}
